import axios from 'axios';

const mockCoupons = [
  {
    id: 'coupon-1',
    title: 'RM20 off RM100+',
    description: 'Get RM20 off a single order over RM100',
    expiry: '2025-12-31',
  },
  {
    id: 'coupon-2',
    title: 'New Member: 5% Off',
    description: 'Enjoy a one-time 5% discount for new members',
    expiry: '2025-12-31',
  },
  {
    id: 'coupon-3',
    title: 'Weekend Flash: RM60 off RM200',
    description: 'Take RM60 off every RM200 spent on weekends, no cap',
    expiry: '2025-12-31',
  },
];

const client = axios.create({
  baseURL: '/gateway/member',
  timeout: 10000,
});

const DEFAULT_PUBLIC_ERROR = 'Request failed, please try again later';
const containsChinese = (value) => /[\u3400-\u9fff]/.test(String(value || ''));

/** Never expose Chinese server messages on the English-only member website. */
const extractMessage = (payload) => {
  const message = payload?.message || payload?.msg || payload?.errorMsg || payload?.error;
  return message && !containsChinese(message) ? String(message) : DEFAULT_PUBLIC_ERROR;
};

const createApiError = (response, fallbackMessage) => {
  const payload = response?.data;
  const error = new Error(extractMessage(payload) || fallbackMessage);
  error.response = response;
  if (payload && typeof payload === 'object' && payload.code != null) {
    error.code = String(payload.code);
  }
  return error;
};

client.interceptors.response.use(
  (response) => {
    const payload = response?.data;
    if (payload && typeof payload === 'object') {
      const success = payload.success;
      const code = payload.code;
      if (success === false) {
        throw createApiError(response, 'Request failed, please try again later');
      }
      if (success === undefined && code && !['OK', 'SUCCESS', '000000'].includes(String(code))) {
        throw createApiError(response, 'Request failed, please try again later');
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
        return payload.data;
      }
    }
    return payload;
  },
  (error) => {
    if (error.response) {
      error.message = extractMessage(error.response.data);
    }
    return Promise.reject(error);
  },
);

// 统一的验证码发送接口，后端会根据配置自动选择验证方式（ycloud, way1, way2）
export function sendVerifyCode(phone, countryCode) {
  return client.post('/send-code', { phone, countryCode });
}

// 已废弃：统一使用 sendVerifyCode，后端会根据配置自动选择验证方式
// 保留此函数以保持向后兼容，但内部调用统一的 sendVerifyCode
export function sendVerifyCodeYCloud(phone, countryCode, channel = 'sms', locale = 'en-US') {
  // 统一使用 /send-code 接口，后端会根据配置自动选择验证方式
  // 如果后端配置为 ycloud，会返回包含 id 的响应
  // 如果后端配置为 way1/way2，会返回 null
  return client.post('/send-code', { phone, countryCode });
}

// 已废弃：使用 registerMemberWithVerification 替代
export function registerMember(name, gender, dateOfBirth, phone, code, referId, countryCode, shopId, campaign) {
  return registerMemberWithVerification(name, gender, dateOfBirth, phone, null, code, referId, countryCode, shopId, campaign);
}

// 统一的注册接口，支持三种验证方式（ycloud, way1, way2）
// 后端会根据配置自动选择验证方式
// verificationId: 可选，仅在 ycloud 模式下需要（如果发送验证码时返回了 id）
export function registerMemberWithVerification(name, gender, dateOfBirth, phone, verificationId, code, referId, countryCode, shopId, campaign) {
  const payload = {
    nickname: name,
    gender,
    birth: dateOfBirth,
    phone,
    code,
    countryCode
  };
  
  // verificationId 仅在 ycloud 模式下需要，way1/way2 模式下不需要
  // 如果提供了 verificationId，则传递给后端（后端会根据配置决定是否使用）
  if (verificationId != null && verificationId !== undefined && verificationId !== '') {
    payload.verificationId = verificationId;
  }
  
  // 后端：id = 店铺，referId = 邀请人会员 id
  if (referId !== undefined && referId !== null && referId !== '') {
    payload.referId = String(referId);
  }
  if (shopId !== undefined && shopId !== null && shopId !== '') {
    payload.id = shopId;
  }
  if (campaign !== undefined && campaign !== null && campaign !== '') {
    payload.campaign = String(campaign);
  }
  
  // 统一使用 /register 接口，后端会根据配置自动选择验证方式
  return client.post('/register', payload);
}

// 保持向后兼容的别名
export const registerMemberWithYCloud = registerMemberWithVerification;

/** WhatsApp 短期 token：YC 网页可能比 yc_get_user_info 更早打开，失败时短暂重试 */
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withWsTokenRetry(requestFn, token, maxAttempts = 6, delayMs = 1500) {
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await requestFn(token);
    } catch (e) {
      lastError = e;
      const msg = e?.message || '';
      if (isWsTokenError(msg) && attempt < maxAttempts) {
        await sleep(delayMs);
        continue;
      }
      throw e;
    }
  }
  throw lastError;
}

function isWsTokenError(message) {
  return (message || '').toLowerCase().includes('invalid or expired token');
}

/** WhatsApp 链接携带的短期 token，对应后端 {@code /gateway/yc_register_ws} */
export function registerMemberFromWhatsApp(token, name, gender, birth) {
  return withWsTokenRetry(
    (wsToken) =>
      client.request({
        method: 'post',
        baseURL: '/gateway',
        url: '/yc_register_ws',
        data: {
          token: wsToken,
          name,
          gender,
          birth,
        },
      }),
    token,
  );
}

/** WhatsApp 短期 token 换长期登录 token，对应后端 {@code /gateway/yc_login_ws} */
export function loginMemberFromWhatsApp(token) {
  return withWsTokenRetry(
    (wsToken) =>
      client.request({
        method: 'post',
        baseURL: '/gateway',
        url: '/yc_login_ws',
        data: { token: wsToken },
      }),
    token,
  );
}

/** 校验 WS 短期 token 是否可走免 OTP 的 /registerfromws */
export function checkWsQuickRegister(token) {
  return client.request({
    method: 'post',
    baseURL: '/gateway',
    url: '/yc_ws_token_check',
    data: { token },
  });
}

export function loginMember(phone, code, countryCode, verificationId = null) {
  const payload = { phone, code, countryCode };
  if (verificationId) {
    payload.verificationId = verificationId;
  }
  return client.post('/login', payload);
}

export function loginByToken(token) {
  if (!token) {
    return Promise.reject(new Error('Token is required'));
  }
  return client.post('/login-by-token', { token });
}

export function logoutMember(token) {
  if (!token) {
    return Promise.resolve(null); // 如果没有 token，直接返回成功
  }
  return client.post('/logout', { token });
}

export function fetchCoupons(userId) {
  if (!userId) {
    return Promise.resolve([]);
  }
  return client.post('/coupon/list', { userId });
}

export function fetchInviteLink(userId) {
  return client.post('/get-invite-link', { userId });
}

export function getSpecialCodeCoupon(userId, specialCode) {
  if (!userId || !specialCode) {
    return Promise.reject(new Error('User ID and special code are required'));
  }
  return client.post('/get-special-code-coupon', {
    userId,
    specialCode: String(specialCode).trim(),
  });
}

/** 固定私域链接领取 TaoGo 19 开业券；loginToken 用于确认当前会员身份。 */
export function claimStoreOpeningOffer(offerToken, loginToken) {
  if (!offerToken || !loginToken) {
    return Promise.reject(new Error('Offer link and member login are required'));
  }
  return client.post('/claim-store-opening-offer', {
    offerToken: String(offerToken).trim(),
    token: loginToken,
  });
}

export function getMemberQRCode(userId) {
  if (!userId) {
    return Promise.reject(new Error('User ID is required'));
  }
  return client.post('/get-member-qrcode', { userId });
}

export function getMemberIdByQRCode(memberCode) {
  if (!memberCode) {
    return Promise.reject(new Error('Member code is required'));
  }
  return client.post('/get-member-id-by-qrcode', { memberCode });
}

export function getExpiredCoupons(userId) {
  if (!userId) {
    return Promise.reject(new Error('User ID is required'));
  }
  return client.post('/get-expired-coupons', { userId });
}

export function signIn(qrCode, memberId) {
  if (!qrCode) {
    return Promise.reject(new Error('QR code is required'));
  }
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/sign', { qrCode, memberId });
}

export function getWorkerQRCode(memberId) {
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/get-worker-qrcode', { memberId });
}

export function bindWorkerByQRCode(workerCode, memberId) {
  if (!workerCode) {
    return Promise.reject(new Error('Worker code is required'));
  }
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/bind-worker-by-qrcode', { workerCode, memberId });
}

export function getUnverifiedGachaProps(memberId) {
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/get-unverified-gacha-props', { memberId });
}

export function scanSpring2026QRCode(qrCode, memberId) {
  if (!qrCode) {
    return Promise.reject(new Error('QR code is required'));
  }
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/scan-spring2026-qrcode', { qrCode, memberId });
}

/** TaoGo Flash：6 档满减免（28/100/200/500/800/1000）合计 RM465，领取窗口 2026-05-27～2026-07-27，券自领取日起 2 个月内有效；每人限领一次 */
export function claimFlashCoupon20260520(memberId) {
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/claim-flash-coupon-20260520', { memberId });
}

export function claimNationalDay2026Prop(memberId) {
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/claim-national-day-2026-prop', {
    memberId,
    token: localStorage.getItem('taogo-member-token'),
  });
}

export function getNationalDay2026Prop(memberId) {
  if (!memberId) {
    return Promise.reject(new Error('Member ID is required'));
  }
  return client.post('/get-national-day-2026-prop', {
    memberId,
    token: localStorage.getItem('taogo-member-token'),
  });
}

export default client;
